import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AccountStatusButton } from "@/components/AccountStatusButton";
import InstallPrompt from "@/components/InstallPrompt";
import { LayoutDashboard, Sparkles, Library, CalendarClock, Layers, Coins, LogOut, Zap, Settings, Gift, ShieldCheck, AlertTriangle } from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/create", label: "Create Video", icon: Sparkles, testid: "nav-create" },
  { to: "/library", label: "Library", icon: Library, testid: "nav-library" },
  { to: "/series", label: "Series", icon: Layers, testid: "nav-series" },
  { to: "/schedule", label: "Schedule", icon: CalendarClock, testid: "nav-schedule" },
  { to: "/credits", label: "Credits", icon: Coins, testid: "nav-credits" },
  { to: "/referrals", label: "Refer & Earn", icon: Gift, testid: "nav-referrals" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items = user?.role === "admin"
    ? [...navItems, { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" }]
    : navItems;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-white/10 bg-[#0A0A0A] fixed h-screen">
        <div className="px-6 h-16 flex items-center border-b border-white/10">
          <div className="flex items-center gap-2" data-testid="app-logo">
            <div className="h-7 w-7 rounded-md bg-blue-600 flex items-center justify-center">
              <Zap className="h-4 w-4 text-white" fill="white" />
            </div>
            <span className="font-head font-extrabold text-lg tracking-tight">Videoileo</span>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-zinc-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <AccountStatusButton />
          <div className="px-3 py-2 mb-2 flex items-center gap-2.5">
            {user?.picture ? (
              <img src={user.picture} alt="" referrerPolicy="no-referrer" data-testid="nav-avatar" className="h-9 w-9 rounded-full border border-white/10 object-cover shrink-0" />
            ) : (
              <div data-testid="nav-avatar-initials" className="h-9 w-9 rounded-full bg-blue-600/25 border border-blue-500/30 flex items-center justify-center text-sm font-bold text-blue-300 shrink-0">
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              <div className="flex items-center gap-1.5 mt-0.5" data-testid="sidebar-credits">
                <Coins className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-sm font-semibold">{user?.credits ?? 0}</span>
                <span className="text-xs text-zinc-500">credits</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-400 hover:bg-white/5 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-black/80 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded bg-blue-600 flex items-center justify-center">
            <Zap className="h-3.5 w-3.5 text-white" fill="white" />
          </div>
          <span className="font-head font-bold">Videoileo</span>
        </div>
        <div className="flex items-center gap-2.5 text-sm">
          <span className="flex items-center gap-1.5"><Coins className="h-4 w-4 text-blue-400" /> {user?.credits ?? 0}</span>
          {user?.picture ? (
            <img src={user.picture} alt="" referrerPolicy="no-referrer" data-testid="m-nav-avatar" className="h-7 w-7 rounded-full border border-white/10 object-cover" />
          ) : (
            <div data-testid="m-nav-avatar-initials" className="h-7 w-7 rounded-full bg-blue-600/25 border border-blue-500/30 flex items-center justify-center text-[11px] font-bold text-blue-300">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 md:ml-64 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen">
        <div className="p-5 md:p-8 max-w-7xl mx-auto">
          {user?.subscription_status === "past_due" && (
            <div data-testid="past-due-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-red-500/40 bg-red-600/10 rounded-md px-5 py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                <p className="text-sm text-zinc-100">
                  <span className="font-semibold text-red-300">Account needs your attention</span> — your last payment didn't go through. Update your payment details to keep your plan active.
                </p>
              </div>
              <NavLink to="/credits?manage=1" data-testid="past-due-fix-btn" className="shrink-0 text-center bg-red-600 hover:bg-red-500 px-4 py-2 rounded-md text-sm font-semibold transition-colors">
                Fix now
              </NavLink>
            </div>
          )}
          {(user?.plan || "free") === "free" && (user?.credits ?? 0) < 60 && (
            <div data-testid="upgrade-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-blue-500/30 bg-blue-600/10 rounded-md px-5 py-4">
              <div className="flex items-center gap-3">
                <Zap className="h-5 w-5 text-blue-400 shrink-0" fill="currentColor" />
                <p className="text-sm text-zinc-200">
                  You're on the <span className="font-medium">Free Trial</span> — {user?.credits ?? 0} credits left. Upgrade to keep creating.
                </p>
              </div>
              <NavLink to="/credits" data-testid="upgrade-banner-btn" className="shrink-0 text-center bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md text-sm font-semibold transition-colors">
                See plans
              </NavLink>
            </div>
          )}
          <Outlet />
        </div>
        {/* mobile bottom nav */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-black/90 backdrop-blur-xl border-t border-white/10 grid pb-[env(safe-area-inset-bottom)]" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={`m-${item.testid}`} className={({ isActive }) =>
              `flex flex-col items-center py-2 text-[10px] ${isActive ? "text-blue-400" : "text-zinc-500"}`}>
              <item.icon className="h-5 w-5 mb-0.5" />
            </NavLink>
          ))}
        </div>
        <InstallPrompt />
      </main>
    </div>
  );
}
